import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FieldsetModule } from 'primeng/fieldset';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';

/**
 * Dumb component: "Información Adicional" de "Crear Empleado/Consultor"
 * (HU1259115, auditoría de fidelidad fase 25/26).
 *
 * <p>Legacy: `CrearEmpleadoViewImpl.ui.xml` líneas 213-267 (`lInfoAdicional`)
 * — Salario, Nivel de Estudio, Cargo, Fecha Inicio/Fin de Contrato. Fuente de
 * los valores: `CrearEmpleadoActivity.consultarNomina()` (`MPSC_DATOS_NOMINA`,
 * tabla externa, distinta de `TPSC_EMPLEADOS`) — se dispara al seleccionar
 * una persona.</p>
 *
 * <p><b>Corrección (fase 26):</b> los 5 campos son SIEMPRE de solo lectura en
 * el legado, con o sin registro de nómina — verificado con grep exhaustivo de
 * `setEnabled(true)`/`setEnable(true)` sobre estos 5 widgets en
 * `CrearEmpleadoViewImpl.java` completo: cero resultados.
 * `setCamposInactivos(false)` (constructor, línea 187, y de nuevo en
 * `reset()`) los deshabilita una única vez y para siempre; los
 * `setEnabled(false)` que aparecen dentro de `setDatosNomina()` son
 * defensivos/redundantes, no una habilitación condicional. Una versión
 * intermedia de esta fase los implementó como condicionalmente editables
 * (si no había registro de nómina) — corregido antes de cerrar la fase: se
 * eliminaron los `@Input()` de deshabilitado por campo y el binding
 * `[(ngModel)]`, quedando 100% solo lectura igual que
 * `InformacionAdicionalEmpleadoComponent` de Modificar.</p>
 *
 * <p>Los valores SÍ viajan en el POST de creación (el backend ya los recibe
 * en `EmpleadoRequest`/`Empleado` vía `CrearEmpleadoFacade.form`, poblados
 * por `cargarDatosNomina()`) — el usuario no los edita, pero se guardan
 * igual que el resto del formulario.</p>
 */
@Component({
  selector: 'app-informacion-adicional-crear-empleado',
  standalone: true,
  imports: [CommonModule, FieldsetModule, InputTextModule, TooltipModule],
  template: `
    <p-fieldset legend="Información Adicional" [toggleable]="true" styleClass="mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Salario</label>
          <input pInputText readonly class="w-full" [value]="salarioDisplay()" pTooltip="Salario" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nivel de Estudio</label>
          <input pInputText readonly class="w-full" [value]="nivelEstudio() ?? ''" pTooltip="Nivel de Estudio" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cargo</label>
          <input pInputText readonly class="w-full" [value]="cargo() ?? ''" pTooltip="Cargo" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha Inicio Contrato (aaaa/mm/dd)</label>
          <input pInputText readonly type="date" class="w-full" [value]="feinicioContrato() ?? ''" pTooltip="Fecha Inicio Contrato" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha Fin Contrato (aaaa/mm/dd)</label>
          <input pInputText readonly type="date" class="w-full" [value]="fefinContrato() ?? ''" pTooltip="Fecha Fin Contrato" />
        </div>
      </div>
    </p-fieldset>
  `,
})
export class InformacionAdicionalCrearEmpleadoComponent {
  readonly salarioDisplay = input.required<string>();
  readonly nivelEstudio = input<string | null>(null);
  readonly cargo = input<string | null>(null);
  readonly feinicioContrato = input<string | null>(null);
  readonly fefinContrato = input<string | null>(null);
}
